import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import { ErrorBoundary } from './ErrorBoundary'
import { SubjectNoiseOverlay, SubjectSquareLayers } from './SubjectSquare'
import {
  loadSubjectAnimation,
  resolveSubjectAnimation,
  resolveSubjectAnimationSegment,
  type LottieAnimationData,
} from './subjectAnimations'

// lottie-web reaches for `document` on import, so defer loading the React
// wrapper until it's actually rendered (client-side, once an animation plays).
// React.lazy + Suspense keeps it out of SSR and out of the initial chunk.
const Lottie = lazy(() => import('lottie-react'))

// Cover the square; stable identity so the Lottie isn't handed a new object each render.
const LOTTIE_RENDERER_SETTINGS = { preserveAspectRatio: 'xMidYMid slice' }

interface SubjectAnimationProps {
  /** Subject slug — selects the matching Lottie file. `brand` / `grayscale`
   *  render the same motion recolored, for non-subject contexts. */
  slug?: string
  /** Whether the parent is hovered/focused — the animation only plays while
   *  this is true; at rest the static brand mark (or `restingMark`) shows. */
  isActive?: boolean
  /** When false, suppresses the film-grain noise overlay. Useful when the
   *  component is rendered inside a scaled container where the noise would
   *  appear magnified. Defaults to true. */
  showNoise?: boolean
  /** Override the resting/loading visual drawn behind the Lottie. Omit for the
   *  default colored concentric brand mark (`SubjectSquareLayers`); pass a custom
   *  node to replace it, or `null` to draw nothing until the animation is ready
   *  (avoids any placeholder flash). */
  restingMark?: ReactNode
}

/**
 * The square subject-brand visual. At rest it shows the static concentric brand
 * mark (the resting design). On hover/focus (`isActive`) the subject's Lottie
 * animation plays on top, looping while active, and reverts on leave.
 *
 * The animation is loaded lazily (a per-slug dynamic `import()`, code-split into
 * its own async chunk) the first time the square is activated — not for squares
 * the user never touches — and cached so later activations replay instantly.
 * Reduced-motion, a missing slug, and a render error in the Lottie all keep the
 * static brand mark. The square holds a fixed aspect ratio whether or not an
 * animation plays, so the Lottie introduces no layout shift.
 */
export const SubjectAnimation = ({
  slug,
  isActive = false,
  showNoise = true,
  restingMark,
}: SubjectAnimationProps) => {
  const prefersReducedMotion = useReducedMotion()
  const animationFile = slug ? resolveSubjectAnimation(slug) : undefined
  const animationSegment = animationFile
    ? resolveSubjectAnimationSegment(animationFile)
    : undefined
  const [shouldLoad, setShouldLoad] = useState(false)
  const [animationData, setAnimationData] =
    useState<LottieAnimationData | null>(null)
  const [didFail, setDidFail] = useState(false)

  // Drop any cached result when the subject (file) changes. Ordered before the
  // latch effect so a square that mounts already-active still loads.
  useEffect(() => {
    setShouldLoad(false)
    setAnimationData(null)
    setDidFail(false)
  }, [animationFile])

  // Latch on first hover/focus so a brief hover still finishes loading and the
  // import only ever runs for squares the user actually interacts with.
  useEffect(() => {
    if (isActive && !prefersReducedMotion) setShouldLoad(true)
  }, [isActive, prefersReducedMotion])

  useEffect(() => {
    if (!animationFile || !shouldLoad || prefersReducedMotion) return undefined
    // Already resolved (or failed) for this file — keep the cached result.
    if (animationData || didFail) return undefined

    let cancelled = false
    loadSubjectAnimation(animationFile)
      ?.then((data) => {
        if (!cancelled) setAnimationData(data)
      })
      .catch(() => {
        if (!cancelled) setDidFail(true)
      })

    return () => {
      cancelled = true
    }
  }, [animationFile, shouldLoad, prefersReducedMotion, animationData, didFail])

  // Play only while active (hover/focus); at rest the static brand mark shows.
  // Unmounting the Lottie on leave restarts it from frame 0 on the next
  // activation and frees the render loop in between.
  const playAnimation =
    isActive && !prefersReducedMotion && !didFail && animationData

  return (
    <div className='relative aspect-square w-full shrink-0 overflow-hidden'>
      {restingMark !== undefined ? (
        restingMark
      ) : (
        <SubjectSquareLayers showNoise={showNoise} />
      )}
      {playAnimation && (
        // A corrupt/invalid animation file can make lottie-web throw on init;
        // isolate that so it falls back to the brand mark instead of taking
        // down the surrounding UI.
        <ErrorBoundary>
          <Suspense fallback={null}>
            <Lottie
              animationData={animationData}
              loop
              autoplay
              initialSegment={animationSegment}
              aria-hidden
              className='absolute inset-0 size-full'
              rendererSettings={LOTTIE_RENDERER_SETTINGS}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {/* Noise overlay sits above the Lottie so it persists during animation. */}
      {playAnimation && showNoise && <SubjectNoiseOverlay />}
    </div>
  )
}
